import React from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Button, Divider } from "@mui/material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import UserBasicDetail from "./userComponents/UserBasicDetail";
import UserEntitiesAndAccess from "./userComponents/UserEntitiesAndAccess";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";

import {
  useDetailUserQuery,
  useEditAccountUserMutation,
} from "store/services/userApi";

import { useAppMeta, useAuth } from "hooks";

const UserDetailContext = React.createContext(null);

export const useUserDetail = () => React.useContext(UserDetailContext);

const UserDetailScreen = () => {
  const { id } = useParams();

  const breadcrumbs = [
    { title: "Users", path: "/app/crm/user/list" },
    { title: "User Detail" },
  ];

  //   RTK Query
  const {
    data: userDetail,
    isLoading: loadingUserDetail,
    isFetching: fetchingUserDetail,
    error,
  } = useDetailUserQuery(id);

  useAppMeta("User Detail");

  return (
    <UserDetailContext.Provider
      value={{
        userDetail,
        loadingUser: loadingUserDetail || fetchingUserDetail,
      }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingUserDetail || fetchingUserDetail}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />

        <BreadcrumbLayout.Paper>
          <UserBasicDetail />

          <Divider />

          {!loadingUserDetail && !fetchingUserDetail ? (
            <>
              <UserEntitiesAndAccess />
            </>
          ) : (
            <RowsSkeleton />
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </UserDetailContext.Provider>
  );
};

export default UserDetailScreen;

const PageActions = () => {
  const { id } = useParams();
  const { userDetail } = useUserDetail();
  const hasAdminRole = userDetail?.entities?.some(entity =>
    entity.authorizations?.some(
      auth => auth.role === "Account Admin" || auth.role === "System Admin"
    )
  );

  return (
    <>
      <Box display="flex" gap={2}>
        <ToggleUserStatus />

        {!hasAdminRole && (<Link to={`/app/crm/user/${id}/change-password`}>
          <Button variant="outlined">Change Password</Button>
        </Link>)}

        <Link to={`/app/crm/user/${id}/edit`}>
          <Button variant="contained">Edit User</Button>
        </Link>
      </Box>
    </>
  );
};

const ToggleUserStatus = () => {
  const [showDialog, setShowDialog] = React.useState(false);

  const { isAccountAdmin, isSystemAdmin } = useAuth();

  const { userDetail } = useUserDetail();

  const userToggleStatus = userDetail?.isActive ? "Deactivate" : "Activate";

  // RTK Query
  const [updateAccountUser, { isLoading: updatingUser }] =
    useEditAccountUserMutation();

  // methods
  const handleUpdateUserStatus = async () => {
    try {
      await updateAccountUser({
        id: userDetail?._id,
        isActive: !userDetail?.isActive,
      }).unwrap();
      toast.info(
        `User successfully ${userDetail?.isActive ? "Disabled" : "Enabled"}!`
      );
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't user client status!");
    }
  };

  if (!isAccountAdmin && !isSystemAdmin) return null;

  return (
    <>
      <Button
        variant="outlined"
        color={userDetail?.isActive ? "error" : "success"}
        disabled={updatingUser}
        onClick={() => setShowDialog(true)}
      >
        {userToggleStatus}
      </Button>

      <AppConfirmDialog
        open={showDialog}
        title={`${userToggleStatus} ${userDetail?.name}?`}
        loading={updatingUser}
        onConfirm={handleUpdateUserStatus}
        onClose={() => setShowDialog(false)}
      >
        <div>
          Are you sure you want to <span>{userToggleStatus}</span>{" "}
          <span className="font-medium text-blue-600 dark:text-blue-200">
            {userDetail?.name}?
          </span>
        </div>
      </AppConfirmDialog>
    </>
  );
};
